/**
 * 공종 템플릿 편집.
 *
 * 화면 둘이 한 파일에 있다 — 목록과 편집기. 편집기는 목록의 한 줄을 여는 것이라
 * 라우트를 나누면 「고치다 만 상태」 가 URL 로 새어 나간다.
 *
 * 상태는 `useReducer` 하나다. 항목 트리는 서버에 한 벌로 저장되므로(저장소가
 * 통째로 덮어쓴다) 줄마다 저장하는 편집은 애초에 불가능하고, 저장 버튼 한 번에
 * 지금 화면이 그대로 굳는다.
 *
 * **렉**: 시드 「필로스 기본」 은 항목이 70 줄이 넘는다. 두 가지로 막는다 —
 *   1. 펼친 공종 하나의 항목만 그린다(나머지는 건수만).
 *   2. 줄(`ItemRow`)은 `memo` 다. 리듀서가 바뀐 줄의 객체만 새로 만들어서,
 *      한 글자 칠 때 다시 그려지는 줄은 **그 줄 하나**다.
 */

import {
  ArrowDown,
  ArrowUp,
  CornerDownRight,
  Plus,
  Trash2,
} from "lucide-react";
import { useRouter } from "@/lib/nav";
import { memo, useReducer, useState, useTransition } from "react";
import { toast } from "sonner";

import EmptyState from "@buildpy/ui/empty-state";
import Pill from "@buildpy/ui/pill";
import SectionCard from "@buildpy/ui/section-card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@buildpy/ui/alert-dialog";
import { Button } from "@buildpy/ui/button";
import { Checkbox } from "@buildpy/ui/checkbox";
import { Input } from "@buildpy/ui/input";
import { Label } from "@buildpy/ui/label";
import { subjectJosa } from "@buildpy/core/lib/josa";
import { settleRedirect } from "@/lib/redirect-error";
import { cn } from "@/lib/utils";
import { isExpr } from "@buildpy/core/modules/quote/qty-expr";
import { deleteTemplate, saveTemplate } from "@/api/actions/template";
import { templateQtyExprError } from "@buildpy/core/modules/template/schema";
import {
  TRADE_GROUP_LABEL,
  TradeCode,
  TradeGroup,
  UNIT_SUGGESTIONS,
} from "@buildpy/core/modules/template/trades";
import { TemplateItem, TemplateTrade, TradeTemplate } from "@buildpy/core/modules/template/types";

/** 고를 수 있는 공종. 「빌드파이 표준 + 이 회사」 를 서버가 합쳐 준다. */
export interface TemplateTradeOption {
  code: string;
  name: string;
  group: TradeGroup;
  sort_order: number;
  /**
   * 새로 고를 수 있는가. **숨긴 공종도 목록에는 온다** — 이미 템플릿에 들어
   * 있으면 이름을 보여 줘야 하기 때문이다. 「공종 추가」 에만 안 뜬다.
   */
  active: boolean;
}

interface Props {
  templates: TradeTemplate[];
  canEdit: boolean;
  /**
   * 내장 15개 상수를 쓰지 않는다 — 회사가 추가한 공종을 템플릿에 못 넣으면
   * 새 견적 뼈대에도 영영 안 들어간다.
   */
  trades: TemplateTradeOption[];
}

const blankItem = (): TemplateItem => ({ scope: "", item: "", unit: "" });

const countItems = (items: TemplateItem[]): number =>
  items.reduce((sum, item) => sum + 1 + countItems(item.children ?? []), 0);

const totalItems = (trades: { items: TemplateItem[] }[]): number =>
  trades.reduce((sum, trade) => sum + countItems(trade.items), 0);

/* ── 편집 상태 ─────────────────────────────────────────────── */

interface EditorTrade {
  trade: TradeCode;
  items: TemplateItem[];
}

interface EditorState {
  id: string | null;
  name: string;
  is_default: boolean;
  trades: EditorTrade[];
  /** 펼친 공종. 한 번에 하나만 그린다. */
  open: TradeCode | null;
}

/** 항목의 자리. `"3"` 은 셋째 줄, `"3.1"` 은 그 줄의 둘째 자식. */
type ItemPath = string;

type ItemField = "scope" | "item" | "unit" | "qty_expr";

type Action =
  | { type: "name"; value: string }
  | { type: "default"; value: boolean }
  | { type: "open"; trade: TradeCode | null }
  | { type: "trade.add"; trade: TradeCode }
  | { type: "trade.remove"; index: number }
  | { type: "trade.move"; index: number; delta: number }
  | { type: "item.add"; index: number }
  | { type: "item.child"; index: number; path: ItemPath }
  | { type: "item.remove"; index: number; path: ItemPath }
  | { type: "item.move"; index: number; path: ItemPath; delta: number }
  | {
      type: "item.field";
      index: number;
      path: ItemPath;
      key: ItemField;
      value: string;
    };

const swap = <T,>(rows: T[], index: number, delta: number): T[] => {
  const next = index + delta;
  if (next < 0 || next >= rows.length) return rows;
  const copy = rows.slice();
  [copy[index], copy[next]] = [copy[next], copy[index]];
  return copy;
};

/**
 * 트리의 한 자리를 고친다. `edit` 은 **그 자리가 속한 형제 목록**과 그 안의
 * 번호를 받아 새 형제 목록을 돌려준다 — 고치기·지우기·옮기기가 다 같은 모양이다.
 */
const atPath = (
  items: TemplateItem[],
  path: number[],
  edit: (rows: TemplateItem[], index: number) => TemplateItem[]
): TemplateItem[] => {
  const [head, ...rest] = path;
  if (rest.length === 0) return edit(items, head);
  return items.map((row, index) =>
    index === head
      ? { ...row, children: atPath(row.children ?? [], rest, edit) }
      : row
  );
};

const toPath = (path: ItemPath): number[] => path.split(".").map(Number);

// SET 부모가 되면 수량이 자식 합이라 식을 버린다.
const withoutQtyExpr = (row: TemplateItem): TemplateItem => {
  const next = { ...row };
  delete next.qty_expr;
  return next;
};

const editTrade = (
  state: EditorState,
  index: number,
  items: (prev: TemplateItem[]) => TemplateItem[]
): EditorState => ({
  ...state,
  trades: state.trades.map((trade, at) =>
    at === index ? { ...trade, items: items(trade.items) } : trade
  ),
});

const reducer = (state: EditorState, action: Action): EditorState => {
  switch (action.type) {
    case "name":
      return { ...state, name: action.value };
    case "default":
      return { ...state, is_default: action.value };
    case "open":
      return { ...state, open: action.trade };

    case "trade.add":
      return state.trades.some((trade) => trade.trade === action.trade)
        ? state
        : {
            ...state,
            trades: [...state.trades, { trade: action.trade, items: [] }],
            open: action.trade,
          };
    case "trade.remove":
      return {
        ...state,
        trades: state.trades.filter((_, at) => at !== action.index),
        open: null,
      };
    case "trade.move":
      return {
        ...state,
        trades: swap(state.trades, action.index, action.delta),
      };

    case "item.add":
      return editTrade(state, action.index, (items) => [
        ...items,
        blankItem(),
      ]);
    case "item.child":
      return editTrade(state, action.index, (items) =>
        atPath(items, toPath(action.path), (rows, at) =>
          rows.map((row, index) =>
            index === at
              ? { ...withoutQtyExpr(row), children: [...(row.children ?? []), blankItem()] }
              : row
          )
        )
      );
    case "item.remove":
      return editTrade(state, action.index, (items) =>
        atPath(items, toPath(action.path), (rows, at) =>
          rows.filter((_, index) => index !== at)
        )
      );
    case "item.move":
      return editTrade(state, action.index, (items) =>
        atPath(items, toPath(action.path), (rows, at) =>
          swap(rows, at, action.delta)
        )
      );
    case "item.field":
      return editTrade(state, action.index, (items) =>
        atPath(items, toPath(action.path), (rows, at) =>
          rows.map((row, index) =>
            index === at ? { ...row, [action.key]: action.value } : row
          )
        )
      );
    default:
      return state;
  }
};

const toEditorState = (template: TradeTemplate | null): EditorState => ({
  id: template?.id ?? null,
  name: template?.name ?? "새 템플릿",
  is_default: template?.is_default ?? false,
  trades: (template?.trades ?? [])
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((trade: TemplateTrade) => ({
      trade: trade.trade,
      items: structuredClone(trade.items),
    })),
  open: null,
});

/* ── 목록 ─────────────────────────────────────────────────── */

/**
 * 복제 이름. 저장소는 조직 안에서 이름이 유일해야 하므로(SQL `unique (org_id, name)`)
 * 「사본」 이 이미 있으면 「사본 2」, 「사본 3」 으로 센다 — 두 번 복제하면 저장이
 * 막히던 자리다.
 */
const copyName = (name: string, taken: readonly string[]): string => {
  const used = new Set(taken);
  const first = `${name} 사본`;
  if (!used.has(first)) return first;

  for (let n = 2; n < 100; n += 1) {
    const candidate = `${first} ${n}`;
    if (!used.has(candidate)) return candidate;
  }
  return `${first} ${Date.now()}`;
};

const SettingsTemplatePanel = ({ templates, canEdit, trades }: Props) => {
  const router = useRouter();
  const [pending, startTransition] = useTransition();
  const [editing, setEditing] = useState<EditorState | null>(null);

  const run = (
    task: () => Promise<{ ok: boolean; message: string }>,
    done?: () => void
  ) =>
    startTransition(async () => {
      try {
        const result = await task();
        if (!result.ok) {
          toast.error(result.message);
          return;
        }
        toast.success(result.message);
        router.refresh();
        done?.();
      } catch (caught) {
        // 세션이 끊기면(401) settleRedirect 가 세션을 지우고 /login 으로 옮긴다 — 토스트로 삼키지 않는다.
        if (settleRedirect(caught)) return;
        toast.error("저장하지 못했습니다. 잠시 후 다시 시도하세요.");
      }
    });

  if (editing) {
    return (
      <TemplateEditor
        initial={editing}
        pending={pending}
        onClose={() => setEditing(null)}
        trades={trades}
        onSave={(state, after) =>
          run(
            () =>
              saveTemplate(state.id, {
                name: state.name,
                is_default: state.is_default,
                trades: state.trades.map((trade) => ({
                  trade: trade.trade,
                  items: trade.items,
                })),
              }).then((result) => {
                if (result.ok) after(result.id);
                return result;
              }),
            undefined
          )
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          새 견적은 기본 템플릿의 뼈대로 시작합니다. 이미 나간 견적은 여기서 무엇을
          고쳐도 그대로입니다.
        </p>
        {canEdit && (
          <Button
            disabled={pending}
            onClick={() => setEditing(toEditorState(null))}
            size="sm"
            type="button">
            <Plus className="size-3.5" />새 템플릿
          </Button>
        )}
      </div>

      {templates.length === 0 ? (
        <EmptyState>
          템플릿이 없습니다.
        </EmptyState>
      ) : (
        <ul className="flex flex-col gap-y-2">
          {templates.map((template) => (
            <SectionCard
              key={template.id}
              as="li"
              className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5"
              padding="none">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-sm font-medium">{template.name}</span>
                {template.is_default && (
                  <Pill tone="outline">기본</Pill>
                )}
                <span className="text-xs text-muted-foreground">
                  공종 {template.trades.length}개 · 항목{" "}
                  {totalItems(template.trades)}건
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-1.5">
                <Button
                  disabled={pending}
                  onClick={() => setEditing(toEditorState(template))}
                  size="sm"
                  type="button"
                  variant="outline">
                  {canEdit ? "편집" : "보기"}
                </Button>

                {canEdit && (
                  <>
                    <Button
                      disabled={pending}
                      onClick={() =>
                        setEditing({
                          ...toEditorState(template),
                          id: null,
                          name: copyName(
                            template.name,
                            templates.map((t) => t.name)
                          ),
                          is_default: false,
                        })
                      }
                      size="sm"
                      type="button"
                      variant="outline">
                      복제
                    </Button>

                    <Button
                      disabled={pending || template.is_default}
                      onClick={() =>
                        run(() =>
                          saveTemplate(template.id, {
                            name: template.name,
                            is_default: true,
                            trades: template.trades.map((trade) => ({
                              trade: trade.trade,
                              items: trade.items,
                            })),
                          })
                        )
                      }
                      size="sm"
                      type="button"
                      variant="outline">
                      기본으로
                    </Button>

                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          disabled={pending || template.is_default}
                          size="sm"
                          type="button"
                          variant="destructive">
                          삭제
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            이 템플릿을 삭제할까요?
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            「{template.name}」{subjectJosa(template.name)} 목록에서 사라집니다. 이미
                            만든 견적은 그대로입니다.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>취소</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => run(() => deleteTemplate(template.id))}>
                            삭제
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                )}
              </div>
            </SectionCard>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SettingsTemplatePanel;

/* ── 편집기 ───────────────────────────────────────────────── */

interface EditorProps {
  initial: EditorState;
  pending: boolean;
  onClose: () => void;
  onSave: (state: EditorState, after: (id: string) => void) => void;
  trades: TemplateTradeOption[];
}

const TemplateEditor = ({
  initial,
  pending,
  onClose,
  onSave,
  trades,
}: EditorProps) => {
  const [state, dispatch] = useReducer(reducer, initial);
  const [savedId, setSavedId] = useState<string | null>(initial.id);

  const used = new Set(state.trades.map((trade) => trade.trade));
  // 숨긴 공종은 새로 고르지 못한다. 이미 들어 있는 것은 위에서 이름으로 보인다.
  const rest = trades.filter(
    (trade) => trade.active && !used.has(trade.code)
  );

  return (
    <div className="flex flex-col gap-y-3">
      {/* 규격 제안은 화면에 한 번만 둔다 — 줄마다 두면 70 벌이 된다. */}
      <datalist id="template-units">
        {UNIT_SUGGESTIONS.map((unit) => (
          <option key={unit} value={unit} />
        ))}
      </datalist>

      <SectionCard as="div" className="flex flex-wrap items-end justify-between gap-2" padding="sm">
        <div className="flex flex-col gap-y-1.5">
          <Label htmlFor="template-name">템플릿 이름</Label>
          <div className="w-64">
            <Input
              disabled={pending}
              id="template-name"
              inputSize="xs"
              name="template-name"
              onChange={(event) =>
                dispatch({ type: "name", value: event.target.value })
              }
              value={state.name}
            />
          </div>
        </div>

        <label className="flex items-center gap-x-2 text-sm">
          <Checkbox
            checked={state.is_default}
            disabled={pending}
            onCheckedChange={(value) =>
              dispatch({ type: "default", value: value === true })
            }
          />
          기본 템플릿으로 쓰기
        </label>

        <div className="flex items-center gap-x-2">
          <span className="text-xs text-muted-foreground">
            공종 {state.trades.length}개 · 항목 {totalItems(state.trades)}건
          </span>
          <Button
            disabled={pending}
            onClick={onClose}
            size="sm"
            type="button"
            variant="outline">
            목록
          </Button>
          <Button
            disabled={pending}
            onClick={() =>
              onSave({ ...state, id: savedId }, (id) => setSavedId(id))
            }
            size="sm"
            type="button">
            저장
          </Button>
        </div>
      </SectionCard>

      {rest.length > 0 && (
        <SectionCard as="div" className="flex flex-wrap items-center gap-1.5" padding="sm">
          <span className="text-xs text-muted-foreground">공종 추가</span>
          {rest.map((trade) => (
            <button
              key={trade.code}
              className="rounded-full border border-border px-2.5 py-1 text-xs hover:bg-muted disabled:opacity-40"
              disabled={pending}
              onClick={() => dispatch({ type: "trade.add", trade: trade.code })}
              type="button">
              + {trade.name}
            </button>
          ))}
        </SectionCard>
      )}

      {state.trades.length === 0 ? (
        <EmptyState>
          공종을 먼저 고르세요.
        </EmptyState>
      ) : (
        <div className="flex flex-col gap-y-2">
          {state.trades.map((trade, index) => (
            <TradeCard
              key={trade.trade}
              dispatch={dispatch}
              index={index}
              isFirst={index === 0}
              isLast={index === state.trades.length - 1}
              isOpen={state.open === trade.trade}
              pending={pending}
              trade={trade}
              trades={trades}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* ── 공종 카드 ────────────────────────────────────────────── */

interface TradeCardProps {
  trades: TemplateTradeOption[];
  trade: EditorTrade;
  index: number;
  isOpen: boolean;
  isFirst: boolean;
  isLast: boolean;
  pending: boolean;
  dispatch: (action: Action) => void;
}

/** 펼친 카드만 항목을 그린다 — 70 줄짜리 템플릿에서 매 글자마다 전부 그리면 밀린다. */
const TradeCard = ({
  trade,
  index,
  isOpen,
  isFirst,
  isLast,
  pending,
  dispatch,
  trades,
}: TradeCardProps) => {
  const def = trades.find((row) => row.code === trade.trade);

  return (
    <SectionCard padding="none">
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
        <button
          className="flex flex-wrap items-center gap-x-2 text-left text-sm font-medium"
          onClick={() =>
            dispatch({ type: "open", trade: isOpen ? null : trade.trade })
          }
          type="button">
          {def?.name ?? trade.trade}
          <span className="text-xs text-muted-foreground">
            {def ? TRADE_GROUP_LABEL[def.group] : ""} ·{" "}
            {countItems(trade.items)}건 {isOpen ? "▾" : "▸"}
          </span>
        </button>

        <div className="flex items-center gap-x-2 md:gap-x-0.5">
          <Button
            aria-label="공종 위로"
            disabled={pending || isFirst}
            onClick={() => dispatch({ type: "trade.move", index, delta: -1 })}
            size="icon-xs"
            type="button"
            variant="ghost-muted">
            <ArrowUp className="size-4" />
          </Button>
          <Button
            aria-label="공종 아래로"
            disabled={pending || isLast}
            onClick={() => dispatch({ type: "trade.move", index, delta: 1 })}
            size="icon-xs"
            type="button"
            variant="ghost-muted">
            <ArrowDown className="size-4" />
          </Button>
          <Button
            aria-label="공종 빼기"
            disabled={pending}
            onClick={() => dispatch({ type: "trade.remove", index })}
            size="icon-xs"
            type="button"
            variant="ghost-destructive">
            <Trash2 className="size-4" />
          </Button>
        </div>
      </div>

      {isOpen && (
        <div className="flex flex-col gap-y-1.5 border-t border-border px-3 py-2">
          {/* lg 미만은 머리줄을 숨기고 줄을 범위|작업내역 / 규격·수량·버튼 두 줄로 접는다(각 입력은 aria-label·placeholder 로 이름이 남는다) */}
          <div className="hidden items-center gap-x-2 text-xs text-muted-foreground lg:grid lg:grid-cols-[1fr_2fr_5rem_7rem_6.5rem]">
            <span>범위</span>
            <span>작업내역</span>
            <span>규격</span>
            <span title="숫자 또는 평수 식 — 예: 평*2.5">수량(식)</span>
            <span className="text-right">줄</span>
          </div>

          {trade.items.length === 0 && (
            <p className="py-2 text-sm text-muted-foreground">
              항목이 없습니다.
            </p>
          )}

          {trade.items.map((item, at) => (
            <ItemBlock
              key={`${trade.trade}-${at}`}
              dispatch={dispatch}
              index={index}
              isFirst={at === 0}
              isLast={at === trade.items.length - 1}
              item={item}
              path={String(at)}
              pending={pending}
            />
          ))}

          <div>
            <Button
              disabled={pending}
              onClick={() => dispatch({ type: "item.add", index })}
              size="sm"
              type="button"
              variant="outline">
              <Plus className="size-3.5" />항목 추가
            </Button>
          </div>
        </div>
      )}
    </SectionCard>
  );
};

/* ── 항목 한 줄(+ SET 자식) ──────────────────────────────── */

interface ItemBlockProps {
  item: TemplateItem;
  index: number;
  path: ItemPath;
  isFirst: boolean;
  isLast: boolean;
  pending: boolean;
  dispatch: (action: Action) => void;
}

const ItemBlock = memo(
  ({ item, index, path, isFirst, isLast, pending, dispatch }: ItemBlockProps) => {
    const children = item.children ?? [];

    return (
      <div className="flex flex-col gap-y-1.5">
        <ItemRow
          depth={0}
          dispatch={dispatch}
          hasChildButton
          index={index}
          isFirst={isFirst}
          isLast={isLast}
          item={item}
          path={path}
          pending={pending}
        />
        {children.map((child, at) => (
          <ItemRow
            key={`${path}-${at}`}
            depth={1}
            dispatch={dispatch}
            hasChildButton={false}
            index={index}
            isFirst={at === 0}
            isLast={at === children.length - 1}
            item={child}
            path={`${path}.${at}`}
            pending={pending}
          />
        ))}
      </div>
    );
  }
);
ItemBlock.displayName = "ItemBlock";

interface ItemRowProps extends ItemBlockProps {
  depth: number;
  hasChildButton: boolean;
}

/**
 * 한 줄. `memo` 라 **이 줄의 객체가 바뀔 때만** 다시 그린다 —
 * 리듀서가 고친 줄만 새 객체로 만들고 나머지는 그대로 들고 오기 때문이다.
 */
const ItemRow = memo(
  ({
    item,
    index,
    path,
    depth,
    hasChildButton,
    isFirst,
    isLast,
    pending,
    dispatch,
  }: ItemRowProps) => {
    const isParent = (item.children ?? []).length > 0;
    const qtyText = item.qty_expr ?? "";
    const qtyError = templateQtyExprError(qtyText);
    const field = (key: ItemField, placeholder: string) => (
      <Input
        aria-label={placeholder}
        className="min-w-0"
        disabled={pending}
        inputSize="xs"
        list={key === "unit" ? "template-units" : undefined}
        name={`item-${key}`}
        onChange={(event) =>
          dispatch({
            type: "item.field",
            index,
            path,
            key,
            value: event.target.value,
          })
        }
        placeholder={placeholder}
        value={item[key] ?? ""}
      />
    );

    return (
      <>
        <div
          className={cn(
            "grid grid-cols-[1fr_1fr_auto] items-center gap-x-2 gap-y-1.5 lg:grid-cols-[1fr_2fr_5rem_7rem_6.5rem]",
            depth > 0 && "pl-6"
          )}>
          {field("scope", "범위")}
          <div className="col-span-2 lg:col-span-1">{field("item", "작업내역")}</div>
          {field("unit", "규격")}
          {isParent ? (
            <span className="px-2 text-xs text-muted-foreground" title="SET 수량은 자식 합입니다">
              자식 합
            </span>
          ) : (
            <div className="relative">
              <Input
                adornment="end"
                aria-invalid={qtyError !== null}
                aria-label="수량(식)"
                className="min-w-0"
                disabled={pending}
                inputMode="text"
                inputSize="xs"
                name="item-qty_expr"
                onChange={(event) =>
                  dispatch({ type: "item.field", index, path, key: "qty_expr", value: event.target.value })
                }
                placeholder="평*2.5"
                title={qtyError ?? (isExpr(qtyText) ? "견적 평수로 계산됩니다" : undefined)}
                value={qtyText}
              />
              {qtyText.trim() !== "" && qtyError === null && isExpr(qtyText) && (
                <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-3xs text-muted-foreground">
                  식
                </span>
              )}
            </div>
          )}

          <div className="flex items-center justify-end gap-x-2 lg:gap-x-0.5">
            {hasChildButton && (
              <Button
                aria-label="SET 자식 추가"
                disabled={pending}
                onClick={() => dispatch({ type: "item.child", index, path })}
                title="SET 자식 추가"
                size="icon-xs"
                type="button"
                variant="ghost-muted">
                <CornerDownRight className="size-4" />
              </Button>
            )}
            <Button
              aria-label="위로"
              disabled={pending || isFirst}
              onClick={() => dispatch({ type: "item.move", index, path, delta: -1 })}
              size="icon-xs"
              type="button"
              variant="ghost-muted">
              <ArrowUp className="size-4" />
            </Button>
            <Button
              aria-label="아래로"
              disabled={pending || isLast}
              onClick={() => dispatch({ type: "item.move", index, path, delta: 1 })}
              size="icon-xs"
              type="button"
              variant="ghost-muted">
              <ArrowDown className="size-4" />
            </Button>
            <Button
              aria-label="줄 삭제"
              disabled={pending}
              onClick={() => dispatch({ type: "item.remove", index, path })}
              size="icon-xs"
              type="button"
              variant="ghost-destructive">
              <Trash2 className="size-4" />
            </Button>
          </div>
        </div>
        {!isParent && qtyError && (
          <p className={cn("text-xs text-destructive", depth > 0 && "pl-6")}>
            수량식 — {qtyError}
          </p>
        )}
      </>
    );
  }
);
ItemRow.displayName = "ItemRow";
